import { z } from 'zod';
import { BOTTLE_STATUSES, CLIMATIZACOES, DRINK_PROFILES, FINALIDADES_DA_ADEGA, PAPEIS_DA_ADEGA, WINE_TYPES, } from './wine.js';
/** Safra minima aceita. Antes disso e quase certo erro de digitacao ou leitura de rotulo. */
const MIN_VINTAGE = 1900;
const maxVintage = () => new Date().getFullYear() + 1;
/**
 * Campo vazio vira `null` — inclusive quando o "vazio" é só espaço.
 *
 * Sem o `trim` aqui, um campo com espaços passava pelo preprocesso, era aparado
 * depois e terminava como string vazia no banco: "não informado" gravado como
 * um valor. Null é o que as telas e as contagens esperam.
 */
const emptyToNull = (schema) => z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? null : value), schema);
export const wineTypeSchema = z.enum(WINE_TYPES);
export const bottleStatusSchema = z.enum(BOTTLE_STATUSES);
export const vintageSchema = emptyToNull(z
    .number({ invalid_type_error: 'Safra deve ser um ano' })
    .int('Safra deve ser um ano inteiro')
    .min(MIN_VINTAGE, `Safra deve ser maior ou igual a ${MIN_VINTAGE}`)
    .refine((year) => year <= maxVintage(), 'Safra não pode ser no futuro')
    .nullable());
/**
 * Teor alcoólico, produção e número da garrafa vêm do rótulo. Aceitam a
 * redação brasileira que a pessoa digitaria ("13,5", "1.350") quando chegam como
 * texto, e número quando chegam da IA.
 */
const decimalFlexivel = (valor) => typeof valor === 'string' && valor.trim() !== ''
    ? Number(valor.replace('%', '').trim().replace(',', '.'))
    : valor;
const inteiroFlexivel = (valor) => typeof valor === 'string' && /\d/.test(valor) ? Number(valor.replace(/\D/g, '')) : valor;
/**
 * Dinheiro como a pessoa digita: "R$ 89,90", "89,90", "1.234,56", "89.9".
 *
 * Quando existe vírgula ela é a casa decimal e o ponto é separador de milhar —
 * a redação brasileira. Sem vírgula, o ponto é o decimal, que é como a API
 * recebe de outro programa.
 */
const precoFlexivel = (valor) => {
    if (typeof valor !== 'string' || valor.trim() === '')
        return valor;
    const limpo = valor.replace(/[^\d.,]/g, '');
    return Number(limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo);
};
/** Ano de janela de guarda: aceita passado (vinho ja no ponto) e futuro razoavel. */
const windowYearSchema = emptyToNull(z.number().int().min(MIN_VINTAGE).max(2200).nullable());
// ---------------------------------------------------------------- Conta
/**
 * Senha mínima de 8 caracteres: curta o bastante para não irritar em teclado de
 * celular, longa o bastante para não ser trivial. O limite superior existe
 * porque o hash é calculado sobre a entrada — sem teto, uma senha gigante vira
 * vetor de negação de serviço.
 */
export const passwordSchema = z
    .string()
    .min(8, 'A senha precisa de pelo menos 8 caracteres')
    .max(200, 'Senha longa demais');
/**
 * O código do convite como a pessoa digita.
 *
 * Tolerante de propósito: ela vai colar com espaço, em minúscula, às vezes com
 * o hífen que a tela mostrou. Normalizar aqui evita o pior tipo de erro — o
 * código certo sendo recusado, sem ninguém entender por quê.
 */
export const inviteCodeSchema = z
    .string()
    .trim()
    .transform((v) => v.toUpperCase().replace(/[\s-]/g, ''))
    .refine((v) => v.length >= 6 && v.length <= 16, 'Código de convite inválido');
export const criarConviteSchema = z.object({
    email: z.string().trim().toLowerCase().email('E-mail inválido').max(160),
});
export const registerSchema = z.object({
    name: z.string().trim().min(2, 'Informe seu nome').max(120),
    email: z.string().trim().toLowerCase().email('E-mail inválido').max(160),
    password: passwordSchema,
    /**
     * Opcional no contrato porque a primeira conta da instalação não tem quem a
     * convide. Quando o cadastro está fechado, o servidor exige — a regra é dele,
     * não do formulário.
     */
    inviteCode: inviteCodeSchema.optional(),
});
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email('E-mail inválido').max(160),
    password: z.string().min(1, 'Informe a senha').max(200),
});
/** Trocar a senha exige a atual: quem pegou o celular desbloqueado não troca sozinho. */
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, 'Informe a senha atual').max(200),
    newPassword: passwordSchema,
})
    .refine((dados) => dados.currentPassword !== dados.newPassword, {
    path: ['newPassword'],
    message: 'A nova senha precisa ser diferente da atual',
});
/**
 * Pedir o link de redefinição.
 *
 * Só o e-mail. A tela não pergunta mais nada, e o servidor responde a mesma
 * coisa exista ou não a conta — ver `recuperacaoDeSenha.service.ts`.
 */
export const esqueciSenhaSchema = z.object({
    email: z.string().trim().toLowerCase().email('E-mail inválido').max(160),
});
/**
 * Redefinir com o token do link.
 *
 * `passwordSchema` é o mesmo da troca de senha comum: quem chega por aqui não
 * ganha o direito de escolher uma senha mais fraca do que escolheria logado.
 */
export const redefinirSenhaSchema = z.object({
    token: z.string().trim().min(1, 'Link inválido').max(200),
    novaSenha: passwordSchema,
});
// ---------------------------------------------------------------- Adega
export const shelfInputSchema = z.object({
    label: z.string().trim().min(1).max(60).optional(),
    capacity: z
        .number()
        .int('Capacidade deve ser um número inteiro')
        .min(1, 'Cada prateleira precisa de pelo menos 1 posição')
        .max(200, 'Máximo de 200 garrafas por prateleira'),
});
/** Ausente ou vazio = não informado, que é diferente de "sem climatização". */
export const climatizacaoSchema = emptyToNull(z.enum(CLIMATIZACOES).nullable());
/** Ausente = não informado. Muda o peso da climatização na análise. */
export const papelSchema = emptyToNull(z.enum(PAPEIS_DA_ADEGA).nullable());
export const cellarSetupSchema = z.object({
    name: z.string().trim().min(1, 'Dê um nome para a adega').max(80).default('Minha Adega'),
    climatizacao: climatizacaoSchema.optional(),
    papel: papelSchema.optional(),
    shelves: z
        .array(shelfInputSchema)
        .min(1, 'Configure pelo menos uma prateleira')
        .max(50, 'Máximo de 50 prateleiras'),
});
/**
 * Edição da adega. Antes existia só para renomear (e sem tela que usasse); com a
 * climatização, os dois campos são opcionais e pelo menos um é obrigatório —
 * mandar `{}` seria uma escrita que não escreve nada.
 */
export const updateCellarSchema = z
    .object({
    name: z.string().trim().min(1, 'Dê um nome para a adega').max(80).optional(),
    climatizacao: climatizacaoSchema.optional(),
    papel: papelSchema.optional(),
})
    .refine((data) => data.name !== undefined || data.climatizacao !== undefined || data.papel !== undefined, { message: 'Informe ao menos um campo para atualizar' });
export const updateShelfSchema = z
    .object({
    label: z.string().trim().min(1).max(60).optional(),
    capacity: z.number().int().min(1).max(200).optional(),
    /**
     * Reduzir capacidade abaixo da ultima posicao ocupada e recusado por padrao.
     * Com force=true as garrafas excedentes sao desvinculadas (viram "sem posicao"),
     * nunca apagadas.
     */
    force: z.boolean().optional().default(false),
})
    .refine((data) => data.label !== undefined || data.capacity !== undefined, {
    message: 'Informe ao menos um campo para atualizar',
});
export const addShelfSchema = shelfInputSchema;
// ---------------------------------------------------------------- Garrafa
export const placementSchema = z.object({
    shelfId: z.number().int().positive().nullable(),
    position: z.number().int().min(0).nullable(),
});
/** Texto opcional vindo da IA: cortado no limite em vez de recusar a leitura inteira. */
const textoLido = (limite) => z
    .string()
    .trim()
    .transform((texto) => texto.slice(0, limite) || null)
    .nullable()
    .optional()
    .transform((texto) => texto ?? null);
/**
 * Ficha do produtor: o que o PRODUTOR escreveu no rótulo, quase sempre no
 * contrarrótulo. É fato declarado por quem fez o vinho — e por isso vale mais,
 * para estimar a guarda, do que um palpite genérico da IA.
 */
export const producerSheetSchema = z.object({
    /** Vinificação e estágio. Ex.: "12 meses em barricas de carvalho francês". */
    winemaking: textoLido(300),
    /** Potencial de guarda como o produtor escreveu. Ex.: "guarda de 8 a 10 anos". */
    agingPotential: textoLido(200),
    /** Ex.: "16 a 18 °C". */
    servingTemperature: textoLido(80),
    /** Harmonização sugerida pelo produtor. */
    pairing: textoLido(300),
    /** Notas de degustação do produtor, resumidas. */
    tastingNotes: textoLido(500),
    /**
     * História do produtor e do vinho contada no rótulo (origem da família, do
     * nome, dos vinhedos). Muito contrarrótulo brasileiro traz isso em vez de ficha
     * técnica — e é informação sobre o produtor escrita por ele mesmo.
     */
    story: textoLido(600),
});
export const bottleCoreSchema = z.object({
    name: z.string().trim().min(1, 'Informe o nome do vinho').max(160),
    producer: emptyToNull(z.string().trim().max(160).nullable()).optional(),
    vintage: vintageSchema.optional(),
    grapeVariety: emptyToNull(z.string().trim().max(160).nullable()).optional(),
    region: emptyToNull(z.string().trim().max(160).nullable()).optional(),
    country: emptyToNull(z.string().trim().max(80).nullable()).optional(),
    type: emptyToNull(wineTypeSchema.nullable()).optional(),
    volumeMl: emptyToNull(z.number().int().min(50).max(30000).nullable()).optional(),
    alcoholPercent: emptyToNull(z.preprocess(decimalFlexivel, z
        .number({ invalid_type_error: 'Teor alcoólico deve ser um número' })
        .min(0, 'Teor alcoólico não pode ser negativo')
        .max(30, 'Teor alcoólico acima de 30% não é de vinho — confira o valor')
        .nullable())).optional(),
    /** Total de garrafas produzidas, quando o rótulo informa. É do vinho. */
    productionSize: emptyToNull(z.preprocess(inteiroFlexivel, z
        .number({ invalid_type_error: 'Produção deve ser um número de garrafas' })
        .int('Produção deve ser um número inteiro de garrafas')
        .min(1, 'Produção deve ser de pelo menos 1 garrafa')
        .max(100_000_000, 'Produção grande demais — confira o valor')
        .nullable())).optional(),
    /** Número desta garrafa na série numerada, como impresso ("0206"). É da garrafa. */
    bottleNumber: emptyToNull(z.string().trim().max(20, 'Número da garrafa com no máximo 20 caracteres').nullable()).optional(),
    /** Vinhedo, parcela, seleção, terroir: o que qualifica o vinho e não cabe nos outros campos. */
    labelDetails: emptyToNull(z.string().trim().max(300, 'Detalhes do rótulo com no máximo 300 caracteres').nullable()).optional(),
    producerSheet: producerSheetSchema.nullable().optional(),
    /**
     * Valor pago nesta garrafa. Não está no rótulo: a leitura de IA nunca preenche
     * — é a pessoa que digita, no cadastro ou depois.
     */
    purchasePrice: emptyToNull(z.preprocess(precoFlexivel, z
        .number({ invalid_type_error: 'Valor pago deve ser um número' })
        .min(0, 'Valor pago não pode ser negativo')
        .max(1_000_000, 'Valor acima de 1 milhão — confira o que foi digitado')
        .nullable())).optional(),
    /** Onde comprou: loja, importadora, vinícola, viagem. */
    purchasePlace: emptyToNull(z.string().trim().max(120).nullable()).optional(),
    notes: emptyToNull(z.string().trim().max(4000).nullable()).optional(),
    drinkFrom: windowYearSchema.optional(),
    drinkUntil: windowYearSchema.optional(),
    agingGuidance: emptyToNull(z.string().trim().max(4000).nullable()).optional(),
    pairingNotes: emptyToNull(z.string().trim().max(4000).nullable()).optional(),
});
const windowOrderRefinement = (data, ctx) => {
    if (typeof data.drinkFrom === 'number' &&
        typeof data.drinkUntil === 'number' &&
        data.drinkFrom > data.drinkUntil) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['drinkUntil'],
            message: 'O fim da janela de guarda não pode ser antes do início',
        });
    }
    // Só compara numeração puramente numérica: "A-12" ou "12/B" existem e não
    // têm como ser comparados com o total.
    if (typeof data.productionSize === 'number' &&
        typeof data.bottleNumber === 'string' &&
        /^\d+$/.test(data.bottleNumber) &&
        Number(data.bottleNumber) > data.productionSize) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bottleNumber'],
            message: 'O número da garrafa não pode ser maior que a produção total',
        });
    }
};
const placementAndPhotoFields = {
    /** A qual adega a garrafa pertence, mesmo quando ainda não tem posição. */
    cellarId: z.number().int().positive().nullable().optional(),
    shelfId: z.number().int().positive().nullable().optional(),
    position: z.number().int().min(0).nullable().optional(),
    photoKey: z.string().trim().max(300).nullable().optional(),
    thumbKey: z.string().trim().max(300).nullable().optional(),
    backPhotoKey: z.string().trim().max(300).nullable().optional(),
};
export const createBottleSchema = bottleCoreSchema
    .extend(placementAndPhotoFields)
    .superRefine(windowOrderRefinement);
export const updateBottleSchema = bottleCoreSchema
    .partial()
    .extend(placementAndPhotoFields)
    .superRefine(windowOrderRefinement);
/**
 * Cópias de uma garrafa já cadastrada — para caixa com várias iguais.
 *
 * Cada item de `destinos` vira uma garrafa: `{ shelfId, position }` para uma vaga,
 * ou `null` para ficar sem posição. As cópias ficam na mesma adega da original.
 */
export const duplicateBottleSchema = z.object({
    destinos: z
        .array(z
        .object({
        shelfId: z.number().int().positive(),
        position: z.number().int().min(0),
    })
        .nullable())
        .min(1, 'Escolha ao menos uma posição, ou "sem posição"')
        .max(48, 'No máximo 48 garrafas iguais de uma vez'),
});
/**
 * "Bebi esta": quando, que nota e o que achou.
 *
 * Tudo opcional — o caminho curto é abrir o app na frente da adega e confirmar.
 * O comentário é do CONSUMO e fica separado de `notes`, que é a observação da
 * garrafa (presente de alguém, ocasião): uma não sobrescreve a outra.
 */
/**
 * Folga para diferença de relógio entre o aparelho e o servidor.
 *
 * O instante vem do celular e é conferido contra o relógio do cluster. Um
 * aparelho alguns segundos adiantado fazia "agora" chegar como futuro, e o
 * consumo do dia era recusado — aconteceu de verdade, e só no celular: em
 * desenvolvimento navegador e servidor são a mesma máquina, com o mesmo
 * relógio. Cinco minutos não escondem erro nenhum (data futura de verdade vem
 * em dias) e cobrem qualquer desencontro plausível.
 */
export const TOLERANCIA_DE_RELOGIO_MS = 5 * 60 * 1000;
export const consumeBottleSchema = z.object({
    consumedAt: z
        .string()
        .datetime()
        .refine((iso) => new Date(iso).getTime() <= Date.now() + TOLERANCIA_DE_RELOGIO_MS, {
        message: 'A data em que você bebeu não pode estar no futuro',
    })
        .optional(),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    note: emptyToNull(z.string().trim().max(2000).nullable()).optional(),
});
export const listBottlesQuerySchema = z.object({
    status: bottleStatusSchema.optional(),
    unplaced: z.coerce.boolean().optional(),
    cellarId: z.coerce.number().int().positive().optional(),
    shelfId: z.coerce.number().int().positive().optional(),
    search: z.string().trim().min(1).max(120).optional(),
});
// ---------------------------------------------------------------- IA
/**
 * Forma que exigimos do modelo ao ler um rotulo.
 * Tudo e nullable de proposito: rotulo borrado, foto cortada ou vinho sem safra
 * sao casos normais — preferimos null explicito a um chute do modelo.
 */
export const labelExtractionSchema = z.object({
    name: z.string().trim().min(1).max(160).nullable(),
    producer: z.string().trim().max(160).nullable(),
    vintage: z.number().int().min(MIN_VINTAGE).max(2200).nullable(),
    grapeVariety: z.string().trim().max(160).nullable(),
    region: z.string().trim().max(160).nullable(),
    country: z.string().trim().max(80).nullable(),
    type: wineTypeSchema.nullable(),
    volumeMl: z.number().int().min(50).max(30000).nullable(),
    alcoholPercent: z.preprocess(decimalFlexivel, z.number().min(0).max(30).nullable()),
    // Campos acrescentados depois: opcionais na resposta, para um modelo que os
    // omita não derrubar a leitura inteira. Tolerantes no formato pelo mesmo motivo.
    bottleNumber: z
        .preprocess((valor) => (typeof valor === 'number' ? String(valor) : valor), z.string().trim().max(20).nullable())
        .optional()
        .default(null),
    productionSize: z
        .preprocess(inteiroFlexivel, z.number().int().min(1).max(100_000_000).nullable())
        .optional()
        .default(null),
    labelDetails: z
        .string()
        .trim()
        .transform((texto) => texto.slice(0, 300) || null)
        .nullable()
        .optional()
        .default(null),
    /**
     * Medalhas, selos e pontuações VISÍVEIS na garrafa, um por item, separados
     * por " · ". Só o que está na foto: premiação "de memória" da IA é recusada
     * pela mesma regra da safra (ver "Premiações: só as que estão na garrafa" em
     * docs/decisoes.md). Lida só na consulta — o cadastro ainda não guarda.
     */
    premiacoes: z
        .string()
        .trim()
        .transform((texto) => texto.slice(0, 300) || null)
        .nullable()
        .optional()
        .default(null),
    // Sempre um objeto depois da validação, mesmo que a IA omita ou mande null:
    // "lida e sem ficha" é informação, e é diferente de "nunca lida".
    producerSheet: producerSheetSchema
        .nullable()
        .optional()
        .transform((ficha) => ficha ?? producerSheetSchema.parse({})),
    confidence: z.number().min(0).max(1).nullable(),
    observacoes: z.string().trim().max(1000).nullable(),
});
/**
 * Leitura de rótulo a partir de fotos JÁ enviadas.
 *
 * Separar "subir a foto" de "ler com IA" é o que torna o cadastro resistente a
 * recarga de página no celular: a foto fica guardada, e a leitura pode ser
 * refeita sem novo upload. Também evita reenviar a frente ao adicionar o verso.
 */
export const readLabelSchema = z.object({
    photoKey: z.string().trim().min(1).max(300),
    backPhotoKey: z.string().trim().min(1).max(300).nullable().optional(),
});
/**
 * Perfil de consumo como a IA devolve. Tolerante de propósito: "Beber jovem" e
 * "guarda-longa" viram o valor canônico, e qualquer coisa fora da lista — ou a
 * chave ausente — vira null. Um perfil ilegível não pode derrubar a resposta
 * inteira, que traz janela, orientação e harmonização.
 */
const perfilDaIa = z
    .preprocess((valor) => typeof valor === 'string'
    ? valor
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
    : valor, z.enum(DRINK_PROFILES).nullable())
    .catch(null);
export const enrichmentSchema = z.object({
    // Vem antes dos anos no prompt: decidido o perfil, a janela segue a decisão.
    perfil: perfilDaIa,
    drinkFrom: z.number().int().min(MIN_VINTAGE).max(2200).nullable(),
    drinkUntil: z.number().int().min(MIN_VINTAGE).max(2200).nullable(),
    // Opcional na resposta: análises antigas e modelos que o omitirem continuam válidos.
    drinkPeak: z.number().int().min(MIN_VINTAGE).max(2200).nullable().optional().default(null),
    agingGuidance: z.string().trim().max(2000),
    pairingNotes: z.string().trim().max(2000),
    servingTemperature: z.string().trim().max(120).nullable(),
    decanting: z.string().trim().max(300).nullable(),
    // Frase curta que explica a janela na lista "Beber antes" — a mesma que a
    // análise em lote já produz. Opcional para não derrubar resposta que a omita.
    justificativa: z
        .string()
        .trim()
        .transform((texto) => texto.slice(0, 300) || null)
        .nullable()
        .optional()
        .default(null),
});
/**
 * Análise de janela de consumo da adega inteira, em lote.
 *
 * A IA só ESTIMA janela e pico por garrafa; quem ordena a lista é o código, em
 * cima do que foi salvo. Pedir para a IA ordenar diretamente daria uma ordem
 * diferente a cada chamada e custaria uma chamada toda vez que a tela abrisse.
 */
export const windowAnalysisItemSchema = z.object({
    bottleId: z.number().int().positive(),
    perfil: perfilDaIa,
    drinkFrom: z.number().int().min(MIN_VINTAGE).max(2200).nullable(),
    drinkUntil: z.number().int().min(MIN_VINTAGE).max(2200).nullable(),
    drinkPeak: z.number().int().min(MIN_VINTAGE).max(2200).nullable(),
    justificativa: z.string().trim().max(300).nullable(),
});
export const windowAnalysisResponseSchema = z.object({
    analises: z.array(windowAnalysisItemSchema),
});
export const analyzeWindowsRequestSchema = z.object({
    /**
     * "sem_informacao" (padrão): só garrafas ainda sem janela — o menor custo.
     * "todas": reanalisa a adega inteira, útil uma vez por ano.
     */
    escopo: z.enum(['sem_informacao', 'todas']).default('sem_informacao'),
});
export const pairingRequestSchema = z.object({
    refeicao: z
        .string()
        .trim()
        .min(3, 'Descreva o que você vai comer')
        .max(500, 'Descrição muito longa'),
});
export const pairingSuggestionSchema = z.object({
    bottleId: z.number().int().positive(),
    score: z.number().min(0).max(100),
    justificativa: z.string().trim().max(1200),
    comoServir: z.string().trim().max(400).nullable(),
});
export const pairingResponseSchema = z.object({
    sugestoes: z.array(pairingSuggestionSchema).max(5),
    comentarioGeral: z.string().trim().max(1000).nullable(),
});
/**
 * Preferências do dono, como elas voltam da API.
 *
 * Lê tolerante de propósito: o valor vem de uma coluna JSON que já existiu sem
 * campo nenhum e vai ganhar perguntas novas. Qualquer coisa fora do esperado
 * (conteúdo antigo, campo que sumiu, `null`) vira "não informado" em vez de
 * derrubar a tela — preferência é contexto, não é dado crítico.
 */
export const preferenciasSchema = z
    .object({
    garrafasPorMes: z.coerce.number().int().min(1).max(200).nullish().catch(null),
    finalidade: z.enum(FINALIDADES_DA_ADEGA).nullish().catch(null),
    evita: z.array(z.string().trim().min(1).max(60)).max(10).catch([]),
    uvasFavoritas: z.array(z.string().trim().min(1).max(60)).max(10).catch([]),
})
    .partial()
    .transform((valor) => ({
    garrafasPorMes: valor.garrafasPorMes ?? null,
    finalidade: valor.finalidade ?? null,
    evita: valor.evita ?? [],
    uvasFavoritas: valor.uvasFavoritas ?? [],
}));
/**
 * O que a tela manda para salvar. Campo ausente fica como está no servidor;
 * `null` em `garrafasPorMes`/`finalidade` é "voltei a não querer informar".
 */
export const atualizarPreferenciasSchema = z
    .object({
    garrafasPorMes: z.number().int().min(1, 'Pelo menos uma garrafa').max(200).nullable().optional(),
    finalidade: z.enum(FINALIDADES_DA_ADEGA).nullable().optional(),
    evita: z.array(z.string().trim().min(1).max(60)).max(10).optional(),
    uvasFavoritas: z.array(z.string().trim().min(1).max(60)).max(10).optional(),
})
    .refine((dados) => Object.keys(dados).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
});
/**
 * Resposta da análise da adega.
 *
 * Tolerante como as outras respostas de IA: lista que vier torta vira lista
 * vazia e o resto continua valendo; texto longo é cortado em vez de derrubar a
 * análise inteira. É conselho, não dado crítico.
 */
const textoDaAnalise = z.string().trim().max(400).catch('');
export const analiseDaAdegaSchema = z.object({
    leitura: z.string().trim().max(1200).catch(''),
    lacunas: z
        .array(z.object({
        titulo: textoDaAnalise,
        porque: textoDaAnalise,
        sugestao: textoDaAnalise,
    }))
        .max(8)
        .catch([]),
    excessos: z
        .array(z.object({ titulo: textoDaAnalise, porque: textoDaAnalise }))
        .max(8)
        .catch([]),
    proximasCompras: z
        .array(z.object({ oQue: textoDaAnalise, porque: textoDaAnalise }))
        .max(8)
        .catch([]),
});
//# sourceMappingURL=schemas.js.map